import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  ViewChild,
} from '@angular/core';

import { ContainerComponent } from '../../../shared/components/container/container.component';
import { PictureComponent } from '../../../shared/components/picture/picture.component';
import { GlobalButtonComponent } from '../../../shared/components/global-button/global-button.component';

import { HelpersService } from '../../../shared/services/helpers.service';
import { appPaths } from '../../../app.paths';
import { CommonModule } from '@angular/common';

export interface resultMessagesType {
  [key: string]: {
    title: string;
    subtitle: string[];
    text: string[];
    footnote: string;
    silymiStatement: string;
  };
}

@Component({
  selector: '[quiz]',
  imports: [
    ContainerComponent,
    PictureComponent,
    GlobalButtonComponent,
    CommonModule,
  ],
  templateUrl: './quiz.component.html',
  styleUrl: './quiz.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    id: 'risk-quiz',
    class: 'bg-primary',
  },
})
export class QuizComponent {
  //libraries
  helpersService = inject(HelpersService);

  //app paths
  appPaths = appPaths;

  quizResult: any;

  //quiz containers
  @ViewChild('introContainer') introContainer!: ElementRef<HTMLElement>;
  @ViewChild('questionsContainer') questionsContainer!: ElementRef<HTMLElement>;
  @ViewChild('resultsContainer') resultsContainer!: ElementRef<HTMLElement>;

  //quiz questions
  quiz = {
    questions: [
      {
        text: "Do you want to learn about you or your loved one's risk?",
        responses: [{ text: 'Mine' }, { text: "My loved one's risk" }],
        footnote:
          "If you'd like to discover the risks for multiple family members, you'll need to take the quiz for one person at a time.",
      },
    ],
  };

  flows = {
    flowA: [
      {
        text: 'How old are you?',
        responses: [{ text: '18-24' }, { text: '25-34' }, { text: '35+' }],
        footnote: '',
      },
      {
        text: 'Does anyone in your family have type 1?',
        responses: [
          { text: 'I have it' },
          {
            text: 'My parent or <br class="d-none d-lg-inline" />sibling has it',
          },
          {
            text: 'Someone else in <br class="d-none d-lg-inline" />my family has it',
          },
          {
            text: 'No one in my <br class="d-none d-lg-inline" />family has it',
          },
        ],
        footnote: '',
      },
      {
        text: 'Do you or does anyone in your family have an autoimmune condition?',
        responses: [{ text: 'Yes' }, { text: 'No' }],
        footnote: `Examples might include celiac disease or Hashimoto's disease.`,
      },
      {
        text: 'Have you ever had certain types of viruses, such as coxsacklevirus B, rotavirus, mumps, or CMV?',
        responses: [{ text: 'Yes' }, { text: 'No' }, { text: "I don't know" }],
        footnote: '',
      },
    ],
    flowB: [
      {
        text: 'How old is your loved one?',
        responses: [
          { text: 'Child (1-9)' },
          { text: 'Pre-teen (10-12)' },
          { text: 'Teenager (13-17)' },
          { text: 'Adult (18+)' },
        ],
        footnote: '',
      },
      {
        text: 'Does anyone in your family have type 1?',
        responses: [
          { text: "I have it/my child's other biological parent has it" },
          { text: 'My child has it' },
          { text: 'Someone else in our family has it' },
          { text: 'No one in our family has it' },
        ],
        footnote: '',
      },
      {
        text: 'Does your loved one or anyone in your family have an autoimmune condition?',
        responses: [{ text: 'Yes' }, { text: 'No' }],
        footnote: '',
      },
      {
        text: 'Has your loved one ever had certain types of viruses, such as coxsacklevirus B, rotavirus, mumps, or CMV?',
        responses: [{ text: 'Yes' }, { text: 'No' }, { text: "I don't know" }],
        footnote: '',
      },
    ],
  };

  //quiz results
  //Flow A Results
  flowAResult1 = {
    title: `Your family could be at a higher risk of developing type 1`,
    subtitle: [
      `Since type 1 diabetes is in your family, your other family members are up to 15x more likely to get it too.`,
    ],
    text: [``],
    footnote: `Knowing the risk is the first step to preparing for the future. Don’t wait for symptoms to start or even a life threatening event to do something about type 1 diabetes. Just 1 blood test can spot type 1 early and could give a doctor options to help.`,
    silymiStatement: `Talk to your doctor to learn more and screen it like you mean it.`,
  };

  flowAResult2 = {
    title: `YOU COULD BE AT A HIGHER RISK OF DEVELOPING type 1`,
    subtitle: [
      `Since someone in your family has type 1 diabetes, you’re up to 15x more likely to get it too. This risk doesn’t go away when you’re an adult. 50% of new type 1 diabetes diagnoses happen after the age of 18.`,
    ],
    text: [``],
    footnote: `Knowing the risk is the first step to preparing for the future. Don’t wait for symptoms, or even a life-threatening event, to do something about type 1 diabetes. Just 1 blood test can spot type 1 early and could give you and your doctor options to help.`,
    silymiStatement: `Talk to your doctor to learn more and screen it like you mean it.`,
  };

  flowAResult3 = {
    title: `YOU COULD BE AT A HIGHER RISK OF DEVELOPING type 1`,
    subtitle: [
      `Your risk could be higher because you or other family members have certain autoimmune conditions, like celiac disease or Hashimoto’s disease.`,
    ],
    text: [
      `Some common viruses could also increase the risk. These include coxsackievirus B, rotavirus (which often causes diarrhea), mumps, or a virus related to herpes called CMV. Talk to your doctor about your history with these types of viruses.`,
    ],
    footnote: `Knowing the risk is the first step to preparing for the future. Don’t wait for symptoms or even a life-threatening event to start to do something about type 1 diabetes. Just 1 blood test can spot type 1 early and give you and your doctor options to help.`,
    silymiStatement: `Talk to your doctor to learn more and screen it like you mean it.`,
  };

  flowAResult4 = {
    title: `THERE IS SOME RISK OF DEVELOPING TYPE 1`,
    subtitle: [
      `About 90% of type 1 diagnoses happen in people with no family history of the disease. It could show up at any age, and many of the risk factors are unknown. Type 1 often catches familles by surprise—and it can be traumatic when it happens.`,
    ],
    text: [``],
    footnote: `Knowing the risk is the first step to preparing for the future. Don’t wait for symptoms, or even a possible life-threatening event, to do something about type 1 diabetes. Just 1 blood test can spot type 1 early and could give your loved one and their doctor options to help.`,
    silymiStatement: `Talk to your doctor to learn more and screen it like you mean it.`,
  };

  //Flow B Results
  flowBResult1 = {
    title: `Your loved one could be at a higher risk of developing type 1`,
    subtitle: [
      `Since someone in your family has type 1 diabetes, your loved one is up to 15x more likely to get it too.`,
    ],
    text: [
      `Their risk could be higher because they or other family members have certain autoimmune conditions, like celiac disease or Hashimoto’s disease. Some common viruses could also increase the risk. These include coxsackievirus B, rotavirus (which often causes diarrhea), mumps, or a virus related to herpes called CMV. Talk to your loved one’s doctor about their history with these types of viruses.`,
    ],
    footnote: `Knowing the risk is the first step to preparing for the future. Don’t wait for symptoms to start, or even a life-threatening event to do something about type 1 diabetes. Just 1 blood test can spot type 1 early and could give your loved one and their doctor options to help.`,
    silymiStatement: `Talk to your loved one's doctor to learn more and screen it like you mean it.`,
  };

  flowBResult2 = {
    title: `Your loved one could be at a higher risk of developing type 1`,
    subtitle: [
      `Since someone in your family has type 1 diabetes, your loved one is up to 15x more likely to get it too.`,
    ],
    text: [``],
    footnote: `Knowing the risk is the first step to preparing for the future. Don’t wait for symptoms to start, or even a life-threatening event to do something about type 1 diabetes. Just 1 blood test can spot type 1 early and could give your loved one and their doctor options to help.`,
    silymiStatement: `Talk to your loved one's doctor to learn more and screen it like you mean it.`,
  };

  flowBResult3 = {
    title: `Your loved one could be at a higher risk of developing type 1`,
    subtitle: [
      `Their risk could be higher because they or other family members have certain autoimmune conditions, like celiac disease or Hashimoto’s disease.`,
    ],
    text: [
      `Some common viruses could also increase the risk. These include coxsackievirus B, (which often causes diarrhea), mumps, or a virus related to herpes called CMV. Talk to your loved doctor about their history with these types of viruses.`,
    ],
    footnote: `Knowing the risk is the first step to preparing for the future. Don’t wait for symptoms, or even a life-threatening event to start to do something about type 1. Just 1 blood test can spot type 1 early and could give your loved one and their doctor options to help.`,
    silymiStatement: `Talk to your loved one's doctor to learn more and screen it like you mean it.`,
  };

  flowBResult4 = {
    title: ` There is some risk of developing type 1`,
    subtitle: [
      `About 90% of type 1 diabetes diagnoses happen in people with no family history of the disease. It could show up at any age, and many of the risk factors are unknown. Type 1 often catches families by surprise.
Knowing the risk is the first step to preparing for the future. Don’t wait for symptoms to start to do something about type 1 diabetes. Just 1 blood test can spot type 1 early—before someone needs insulin.`,
    ],
    text: [``],
    footnote: ``,
    silymiStatement: `Talk to your loved one's doctor to learn more and screen it like you mean it.`,
  };

  resultMessages: resultMessagesType = {
    //Flow A Result 1
    '0_0_0_0_0': this.flowAResult1,
    '0_0_0_0_1': this.flowAResult1,
    '0_0_0_0_2': this.flowAResult1,
    '0_0_0_1_0': this.flowAResult1,
    '0_0_0_1_1': this.flowAResult1,
    '0_0_0_1_2': this.flowAResult1,
    //Flow A Result 2
    '0_0_1_0_0': this.flowAResult2,
    '0_0_1_0_1': this.flowAResult2,
    '0_0_1_0_2': this.flowAResult2,
    '0_0_1_1_0': this.flowAResult2,
    '0_0_1_1_1': this.flowAResult2,
    '0_0_1_1_2': this.flowAResult2,
    '0_0_2_0_0': this.flowAResult2,
    '0_0_2_0_1': this.flowAResult2,
    '0_0_2_0_2': this.flowAResult2,
    '0_0_2_1_0': this.flowAResult2,
    '0_0_2_1_1': this.flowAResult2,
    '0_0_2_1_2': this.flowAResult2,
    //Flow A Result 3
    '0_0_3_0_0': this.flowAResult3,
    '0_0_3_0_1': this.flowAResult3,
    '0_0_3_0_2': this.flowAResult3,
    //Flow A Result 4
    '0_0_3_1_0': this.flowAResult4,
    '0_0_3_1_1': this.flowAResult4,
    '0_0_3_1_2': this.flowAResult4,
    //Flow B Result 1
    '1_0_0_0_0': this.flowBResult1,
    '1_0_0_0_1': this.flowBResult1,
    '1_0_0_0_2': this.flowBResult1,
    '1_0_0_1_0': this.flowBResult1,
    '1_0_1_0_0': this.flowBResult1,
    '1_0_1_0_1': this.flowBResult1,
    '1_0_1_0_2': this.flowBResult1,
    '1_0_1_1_0': this.flowBResult1,
    '1_0_2_0_0': this.flowBResult1,
    '1_0_2_0_1': this.flowBResult1,
    '1_0_2_0_2': this.flowBResult1,
    '1_0_2_1_0': this.flowBResult1,
    //Flow B Result 2
    '1_0_0_1_1': this.flowBResult2,
    '1_0_0_1_2': this.flowBResult2,
    '1_0_1_1_1': this.flowBResult2,
    '1_0_1_1_2': this.flowBResult2,
    '1_0_2_1_1': this.flowBResult2,
    '1_0_2_1_2': this.flowBResult2,
    //Flow B Result 3
    '1_0_3_0_0': this.flowBResult3,
    '1_0_3_0_1': this.flowBResult3,
    '1_0_3_0_2': this.flowBResult3,
    '1_0_3_1_0': this.flowBResult3,
    //Flow B Result 4
    '1_0_3_1_1': this.flowBResult4,
    '1_0_3_1_2': this.flowBResult4,
  };

  show: boolean = false;
  results: boolean = false;
  actorPortrayalPosition: 'left' | 'right' = 'left';
  questionImg: any = {
    image: 'whatsyourtd1risk/question-img-1',
    altext: 'A father wrapping his arm around his son',
    format: '',
    width: '429',
    height: '551',
    mobile: true,
    webp: true,
    classes: 'img-fluid',
    imageRight: '20px',
    actorMarginLeft: '-30px',
    actorMarginRight: '0px'
  };
  questionIndex: number = 0;
  questionLoaded: boolean = false;
  // quizResult!: any;
  userResponses: Array<number> = [];

  animateContainers() {
    //validate platform
    if (!this.helpersService.validatePlatform()) return;

    if (!this.show) {
      this.introContainer.nativeElement.classList.add('fade-out-left');
      setTimeout(() => {
        this.introContainer.nativeElement.classList.add('d-none');
        this.questionsContainer.nativeElement.classList.add('fade-in-right');
        this.questionsContainer.nativeElement.classList.remove('d-none');
      }, 500);

      this.show = true;
    } else {
      if (this.results) {
        this.questionsContainer.nativeElement.classList.add('fade-out-left');
        setTimeout(() => {
          this.questionsContainer.nativeElement.classList.add('d-none');
          this.resultsContainer.nativeElement.classList.add('fade-in-right');
          this.resultsContainer.nativeElement.classList.remove('d-none');
        }, 500);
        this.results = false;
      } else {
        // fade out animations
        this.questionsContainer.nativeElement.classList.add('fade-out-left');
        this.resultsContainer.nativeElement.classList.add('fade-out-left');

        setTimeout(() => {
          // fade in to the first page
          this.introContainer.nativeElement.classList.remove('fade-out-left');
          this.introContainer.nativeElement.classList.remove('d-none');
          this.introContainer.nativeElement.classList.add('fade-in-right');

          // hide questions container
          this.questionsContainer.nativeElement.classList.remove(
            'fade-out-left'
          );
          this.questionsContainer.nativeElement.classList.remove(
            'fade-in-right'
          );
          this.questionsContainer.nativeElement.classList.add('d-none');

          // hide results container
          this.resultsContainer.nativeElement.classList.remove('fade-out-left');
          this.resultsContainer.nativeElement.classList.remove('fade-in-right');
          this.resultsContainer.nativeElement.classList.add('d-none');
        }, 500);

        this.results = false;
        this.show = false;
      }
    }
  }

  //quiz functions
  getCurrentQuestionText() {
    return this.quiz.questions[this.questionIndex].text;
  }

  getCurrentResponses() {
    return this.quiz.questions[this.questionIndex].responses;
  }

  getCurrentQuestionFootnote() {
    return this.quiz.questions[this.questionIndex].footnote;
  }

  setCurrentQuestionPicture() {
    let imgName = 'question-img-1';
    let altTag = '';
    let imgWidth = '429';
    let imgHeight = '551';
    let imageRight = '20px';
    let actorMarginLeft = '-30px';
    let actorMarginRight = '0px';
    this.actorPortrayalPosition = 'left';

    switch (this.questionIndex) {
      case 0:
        imgName = 'question-img-1';
        altTag = 'A father wrapping his arm around his son';
        imgWidth = '429';
        imgHeight = '551';
        imageRight = '20px';
        actorMarginLeft = '-30px';
        actorMarginRight = '0px';
        this.actorPortrayalPosition = 'left';
        break;

      case 1:
        imgName = 'question-img-2';
        altTag = 'A woman standing with her arms crossed';
        imgWidth = '340';
        imgHeight = '508';
        imageRight = '40px';
        actorMarginLeft = '0px';
        actorMarginRight = '-40px';
        this.actorPortrayalPosition = 'right';
        break;

      case 2:
      case 3:
        imgName = 'question-img-3';
        altTag = 'A mother and son both standing with their arms crossed';
        imgWidth = '454';
        imgHeight = '529';
        imageRight = '20px';
        actorMarginLeft = '-110px';
        actorMarginRight = '0px';
        this.actorPortrayalPosition = 'left';
        break;

      case 4:
        if (this.userResponses[0] === 0) {
          imgName = 'question-img-4-you';
          altTag = 'A man standing smiling';
          imgWidth = '584';
          imgHeight = '590';
          imageRight = '10px';
          actorMarginLeft = '0px';
          actorMarginRight = '0px';
          this.actorPortrayalPosition = 'right';
        } else {
          imgName = 'question-img-4-loved';
          altTag = 'A mother standing with her daughter in front of her';
          imgWidth = '504';
          imgHeight = '567';
          imageRight = '10px';
          actorMarginLeft = '-10px';
          actorMarginRight = '0px';
          this.actorPortrayalPosition = 'left';
        }
        break;

      default:
        imgName = 'question-img-1';
        altTag = 'A mother wrapping her arms around her daughter and both are smiling';
        imgWidth = '429';
        imgHeight = '551';
        imageRight = '20px';
        actorMarginLeft = '-30px';
        actorMarginRight = '0px';
        this.actorPortrayalPosition = 'left';
        break;
    }

    this.questionImg = {
      image: 'whatsyourtd1risk/' + imgName,
      altext: altTag,
      format: '',
      width: imgWidth,
      height: imgHeight,
      mobile: true,
      webp: true,
      classes: 'img-fluid',
      imageRight: imageRight,
      actorMarginLeft: actorMarginLeft,
      actorMarginRight: actorMarginRight
    };
  }

  selectOption(responseIndex: number) {
    // response is assigned to responses array
    this.userResponses[this.questionIndex] = responseIndex;

    // questions are loeaded depending of response
    if (!this.questionLoaded) {
      if (this.quiz.questions.length <= 1) {
        if (this.userResponses[this.questionIndex] === 0) {
          this.flows.flowA.forEach((q) => this.quiz.questions.push(q));
        } else {
          this.flows.flowB.forEach((q) => this.quiz.questions.push(q));
        }
        this.questionLoaded = true;
      }
    }
  }

  prevQuestion() {
    if (this.questionIndex > 0) {
      this.questionIndex--;
      this.setCurrentQuestionPicture();
      this.questionLoaded = this.questionIndex !== 0;

      if (this.questionIndex == 0) {
        // reset questions to the first only
        this.quiz.questions.splice(1, this.quiz.questions.length - 1);
        // reset user responses
        this.userResponses = [];
      }
    } else {
      this.resetQuiz();
    }
  }

  nextQuestion() {
    if (this.questionIndex < this.quiz.questions.length) {
      if (this.questionIndex === this.quiz.questions.length - 1) {
        this.getResults();
      }
    }
    if (this.questionIndex < this.quiz.questions.length - 1) {
      this.questionIndex++;
      this.setCurrentQuestionPicture();
    }
  }

  getResults() {
    this.results = true;

    const [answer0, answer1, answer2, answer3, answer4] = this.userResponses;
    const key = `${answer0}_0_${answer2}_${answer3}_${answer4}`;
    // console.log(key);
    // this.resultMessages[key];
    if (key in this.resultMessages) {
      this.quizResult = this.resultMessages[key];
      // console.log(this.quizResult);
    } else {
      console.warn(`Key "${key}" not found in resultMessages`);
    }

    // animate boxes
    this.animateContainers();
  }

  resetQuiz() {
    // animations
    this.animateContainers();

    // reset questions to the first one
    this.quiz.questions.splice(1, this.quiz.questions.length - 1);

    // reset user responses
    this.userResponses = [];

    // reset questions loaded
    this.questionLoaded = false;

    // reset question index
    this.questionIndex = 0;

    // reset question picture
    this.setCurrentQuestionPicture();

    // validate platform
    if (!this.helpersService.validatePlatform()) return;

    //scroll back up to quiz
    const jumpTo = this.helpersService.doc.querySelector('section[jump-to]');
    const jumpToHeight = jumpTo?.clientHeight;
    this.helpersService.scrollToItem('risk-quiz', jumpToHeight);
  }
}
